import React, { useCallback, useEffect } from "react";
import { useSetAtom } from "jotai";
import { EnlargeAvatarData, enlargeAvatarData } from "../../atoms/avatar";
import { Image, Platform, Pressable, StyleSheet, useWindowDimensions } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import CachedImage from "../../components/CachedImage";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { scheduleOnRN } from "react-native-worklets";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const AnimatedImageComponent = Animated.createAnimatedComponent(Image);
const AnimatedCachedImageComponent = Animated.createAnimatedComponent(CachedImage);

const ANIMATION_DURATION = 300;
const MAX_SIZE = 700;

const IMAGE_PADDING = 8;

const BACKGROUND_COLOR = ["rgba(255, 255, 255, 0)", "rgba(255, 255, 255, 1)"];

type EnlargedUserAvatarProps = EnlargeAvatarData;

const EnlargedUserAvatar = ({
    image: { avatarPath, props },
    initialPosition,
}: EnlargedUserAvatarProps) => {
    const setEnlargeAvatarAtom = useSetAtom(enlargeAvatarData);
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const { top } = useSafeAreaInsets();

    const topOffset = Platform.OS === "android" ? -top : 0;

    const initialY = initialPosition.pageY - topOffset;
    const windowHeightMinusTopInset = windowHeight - topOffset;

    // Background props
    const backgroundColor = useSharedValue(BACKGROUND_COLOR[0]);

    const backgroundAnimatedStyles = useAnimatedStyle(() => ({
        backgroundColor: backgroundColor.value,
    }));

    // Image props
    const pageX = useSharedValue(initialPosition.pageX);
    const pageY = useSharedValue(initialY);
    const size = useSharedValue(initialPosition.size);
    const borderRadius = useSharedValue(initialPosition.size / 2);

    const animatedStyles = useAnimatedStyle(() => ({
        transform: [{ translateX: pageX.value }, { translateY: pageY.value }],
        width: size.value,
        height: size.value,
        borderRadius: borderRadius.value,
    }));

    useEffect(() => {
        backgroundColor.value = withTiming(BACKGROUND_COLOR[1], {
            duration: ANIMATION_DURATION,
        });

        const isWidthSmallerSide = windowWidth < windowHeightMinusTopInset;

        const smallerSize = isWidthSmallerSide ? windowWidth : windowHeightMinusTopInset;
        let enlargedSize = smallerSize - IMAGE_PADDING * 2;
        if (enlargedSize > MAX_SIZE) {
            enlargedSize = MAX_SIZE;
        }

        const padding = smallerSize / 2 - enlargedSize / 2;

        size.value = withTiming(enlargedSize, { duration: ANIMATION_DURATION });
        borderRadius.value = withTiming(enlargedSize / 2, { duration: ANIMATION_DURATION });

        pageX.value = withTiming(
            isWidthSmallerSide ? padding : windowWidth / 2 - enlargedSize / 2,
            { duration: ANIMATION_DURATION },
        );
        pageY.value = withTiming(
            isWidthSmallerSide ? windowHeightMinusTopInset / 2 - enlargedSize / 2 : padding,
            {
                duration: ANIMATION_DURATION,
            },
        );
    }, [backgroundColor, borderRadius, pageX, pageY, size, windowHeightMinusTopInset, windowWidth]);

    const onCloseAnimationFinished = useCallback(() => {
        setEnlargeAvatarAtom(null);
    }, [setEnlargeAvatarAtom]);

    const onClose = useCallback(() => {
        backgroundColor.value = withTiming(BACKGROUND_COLOR[0], {
            duration: ANIMATION_DURATION,
        });

        size.value = withTiming(initialPosition.size, { duration: ANIMATION_DURATION });
        borderRadius.value = withTiming(initialPosition.size / 2, { duration: ANIMATION_DURATION });

        pageX.value = withTiming(initialPosition.pageX, { duration: ANIMATION_DURATION });
        pageY.value = withTiming(initialY, { duration: ANIMATION_DURATION }, (finished) => {
            if (finished) {
                scheduleOnRN(onCloseAnimationFinished);
            }
        });
    }, [
        backgroundColor,
        borderRadius,
        initialPosition.pageX,
        initialPosition.size,
        initialY,
        onCloseAnimationFinished,
        pageX,
        pageY,
        size,
    ]);

    const AnimatedComponent =
        typeof avatarPath === "number" ? AnimatedImageComponent : AnimatedCachedImageComponent;

    return (
        <AnimatedPressable onPress={onClose} style={[styles.container, backgroundAnimatedStyles]}>
            <AnimatedComponent
                {...props}
                sourceAnimationDuration={0}
                thumbnailAnimationDuration={0}
                resizeMethod="scale"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                source={avatarPath}
                style={[props.style, animatedStyles]}
            />
        </AnimatedPressable>
    );
};

const styles = StyleSheet.create({
    container: {
        position: "absolute",
        width: "100%",
        height: "100%",
    },
});

export default EnlargedUserAvatar;
